import { normalizeAdvisorCode } from "@/lib/advisorCode";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  listAllProfiles,
  listAllRoles,
  addUserRole,
  removeUserRole,
  listAllAdvisorCodes,
  createAdvisorCode,
  setAdvisorCodeActive,
  type ProfileRow,
  type RoleRow,
  type AdvisorCodeRow,
} from "@/lib/admin";
import type { AppRole } from "@/hooks/useRoles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ROLES: AppRole[] = ["client", "advisor", "admin"];

export default function Admin() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [codes, setCodes] = useState<AdvisorCodeRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [newCode, setNewCode] = useState("");
  const [newCodeAdvisor, setNewCodeAdvisor] = useState("");
  const [newCodeNote, setNewCodeNote] = useState("");

  const refresh = async () => {
    try {
      const [p, r, c] = await Promise.all([listAllProfiles(), listAllRoles(), listAllAdvisorCodes()]);
      setProfiles(p);
      setRoles(r);
      setCodes(c);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const rolesByUser = useMemo(() => {
    const m = new Map<string, Set<AppRole>>();
    for (const r of roles) {
      if (!m.has(r.user_id)) m.set(r.user_id, new Set());
      m.get(r.user_id)!.add(r.role);
    }
    return m;
  }, [roles]);

  const advisors = useMemo(
    () => profiles.filter((p) => rolesByUser.get(p.id)?.has("advisor")),
    [profiles, rolesByUser],
  );

  const toggleRole = async (userId: string, role: AppRole, on: boolean) => {
    try {
      if (on) await addUserRole(userId, role);
      else await removeUserRole(userId, role);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const onCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    // Issue the code in the same shape clients will be matched on, so
    // "rc100" and "RC 100" can never become a second, separate advisor.
    const code = normalizeAdvisorCode(newCode);
    if (!code || !newCodeAdvisor) {
      toast.error("Code and advisor required");
      return;
    }
    try {
      await createAdvisorCode({
        code,
        advisor_user_id: newCodeAdvisor,
        discount_note: newCodeNote.trim() || null,
      });
      setNewCode(""); setNewCodeNote("");
      toast.success("Advisor code created");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create code");
    }
  };

  const advisorLabel = (id: string) => {
    const p = profiles.find((x) => x.id === id);
    return p?.display_name || p?.email || id.slice(0, 8);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Panel — Family Loan Planner</title>
        <meta name="description" content="Internal admin panel for managing users, roles, and advisor codes." />
      </Helmet>
      <header className="border-b bg-primary text-primary-foreground">
        <div className="container mx-auto h-16 px-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">Family Loan Planner™ · Admin</Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="opacity-80 hidden sm:inline">{user?.email}</span>
            <Button variant="secondary" size="sm" onClick={async () => { await signOut(); nav("/"); }}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8 max-w-5xl">
        <h1 className="text-2xl font-semibold">Admin Panel</h1>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {/* Users & roles */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Users &amp; roles</h2>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Email</th>
                  {ROLES.map((r) => <th key={r} className="text-center p-3">{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => {
                  const set = rolesByUser.get(p.id) ?? new Set<AppRole>();
                  return (
                    <tr key={p.id} className="border-t">
                      <td className="p-3">{p.display_name || "—"}</td>
                      <td className="p-3 text-muted-foreground">{p.email}</td>
                      {ROLES.map((r) => {
                        const on = set.has(r);
                        return (
                          <td key={r} className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={(e) => toggleRole(p.id, r, e.target.checked)}
                              aria-label={`${p.email} ${r}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </section>

        {/* Create advisor code */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Create advisor code</h2>
          <Card className="p-4">
            <form onSubmit={onCreateCode} className="grid sm:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs">Code</Label>
                <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="ADV-2026-001" />
              </div>
              <div>
                <Label className="text-xs">Advisor</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={newCodeAdvisor}
                  onChange={(e) => setNewCodeAdvisor(e.target.value)}
                >
                  <option value="">Select advisor…</option>
                  {advisors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name || a.email || a.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-xs">Discount note (optional)</Label>
                <Input value={newCodeNote} onChange={(e) => setNewCodeNote(e.target.value)} placeholder="20% off" />
              </div>
              <Button type="submit">Create code</Button>
            </form>
            {advisors.length === 0 && (
              <p className="text-xs text-muted-foreground mt-3">Assign the advisor role above to make users selectable here.</p>
            )}
          </Card>
        </section>

        {/* Existing codes */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Advisor codes</h2>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Advisor</th>
                  <th className="text-left p-3">Note</th>
                  <th className="text-left p-3">Created</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-t">
                    <td className="p-3 font-mono">{c.code}</td>
                    <td className="p-3">{advisorLabel(c.advisor_user_id)}</td>
                    <td className="p-3 text-muted-foreground">{c.discount_note || "—"}</td>
                    <td className="p-3 text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <Badge variant={c.active ? "default" : "secondary"}>{c.active ? "Active" : "Inactive"}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await setAdvisorCodeActive(c.code, !c.active);
                            await refresh();
                          } catch (e) {
                            toast.error(e instanceof Error ? e.message : "Failed");
                          }
                        }}
                      >
                        {c.active ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  </tr>
                ))}
                {codes.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No advisor codes yet.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </section>
      </main>
    </div>
  );
}